import { CourtsResponse } from '@/store/apis/hosted-filters-api'

export type FederalCheckboxOption = {
  id: string
  label: string
  value: {
    federalCourtJurisdictionCodes: string[]
  }
}

export type FederalCheckboxOptionWithChildren = FederalCheckboxOption & {
  jurisdictions: Record<string, string | string[]> // string array for bankruptcy courts
}

/**
 * Extracts top-level federal courts (Supreme Court, Federal Circuit, etc.)
 * These are courts that don't belong to specific circuits.
 *
 * @param courtsData - The courts data loaded from API or JSON
 * @returns Array of FederalCheckboxOption for top-level federal courts
 */
export function getTopLevelFederalCourts(courtsData: CourtsResponse): FederalCheckboxOption[] {
  const federalData = courtsData.Federal as Record<string, string | Record<string, string | string[]>>
  const topLevelCourts: FederalCheckboxOption[] = []

  // Filter entries where the value is a string (top-level courts)
  for (const [courtName, courtValue] of Object.entries(federalData)) {
    if (typeof courtValue === 'string') {
      topLevelCourts.push({
        id: courtName,
        label: courtName,
        value: {
          federalCourtJurisdictionCodes: [courtValue],
        },
      })
    }
  }

  return topLevelCourts
}

/**
 * Extracts circuit-based federal courts with their district and bankruptcy courts.
 *
 * @param courtsData - The courts data loaded from API or JSON
 * @returns Array of FederalCheckboxOptionWithChildren for circuit courts
 */
export function getCircuitBasedFederalCourts(courtsData: CourtsResponse): FederalCheckboxOptionWithChildren[] {
  const federalData = courtsData.Federal as Record<string, string | Record<string, string | string[]>>
  const circuitCourts: FederalCheckboxOptionWithChildren[] = []

  // Filter entries where the value is an object (circuit courts with children)
  for (const [circuitName, courtValue] of Object.entries(federalData)) {
    if (typeof courtValue === 'object' && courtValue !== null) {
      const jurisdictions = courtValue as Record<string, string | string[]>
      const allJurisdictionCodes: string[] = []

      // Collect all jurisdiction codes from this circuit
      for (const jurisdiction of Object.values(jurisdictions)) {
        if (Array.isArray(jurisdiction)) {
          allJurisdictionCodes.push(...jurisdiction)
        } else {
          allJurisdictionCodes.push(jurisdiction)
        }
      }

      circuitCourts.push({
        id: circuitName,
        label: circuitName,
        value: {
          federalCourtJurisdictionCodes: allJurisdictionCodes,
        },
        jurisdictions,
      })
    }
  }

  return circuitCourts
}
